import React, { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { getWorkflows, createWorkflow, updateWorkflow, deleteWorkflow } from '../services/api';
import './Workflows.css';

const statusBadge = s => s === 'Active' ? 'badge badge-green' : s === 'Paused' ? 'badge badge-amber' : 'badge badge-gray';

export default function Workflows({ toast }) {
  const { token } = useApp();
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [creating, setCreating]   = useState(false);
  const [newWf, setNewWf]         = useState({ name:'', trigger:'', description:'' });

  const load = async () => {
    setLoading(true);
    try {
      const res = await getWorkflows(token);
      setWorkflows(res.data || []);
    } catch (e) { toast?.(e.message, 'error'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [token]);

  const handleCreate = async () => {
    if (!newWf.name.trim()) { toast?.('Enter a workflow name', 'error'); return; }
    try {
      await createWorkflow(token, { ...newWf, status: 'Active' });
      toast?.('Workflow created');
      setCreating(false);
      setNewWf({ name:'', trigger:'', description:'' });
      load();
    } catch (e) { toast?.(e.message, 'error'); }
  };

  const toggleStatus = async (wf) => {
    try {
      await updateWorkflow(token, wf.id, { status: wf.status === 'Active' ? 'Paused' : 'Active' });
      toast?.('Workflow updated');
      load();
    } catch (e) { toast?.(e.message, 'error'); }
  };

  const handleDelete = async (id) => {
    try {
      await deleteWorkflow(token, id);
      toast?.('Workflow deleted');
      load();
    } catch (e) { toast?.(e.message, 'error'); }
  };

  return (
    <div>
      <div className="stats-grid">
        <div className="stat-card"><div className="stat-label">Total workflows</div><div className="stat-value">{workflows.length}</div><div className="stat-sub">All workflows</div></div>
        <div className="stat-card"><div className="stat-label">Active</div><div className="stat-value">{workflows.filter(w=>w.status==='Active').length}</div><div className="stat-sub stat-up">Running</div></div>
        <div className="stat-card"><div className="stat-label">Paused</div><div className="stat-value">{workflows.filter(w=>w.status==='Paused').length}</div><div className="stat-sub">On hold</div></div>
        <div className="stat-card"><div className="stat-label">Draft</div><div className="stat-value">{workflows.filter(w=>w.status==='Draft').length}</div><div className="stat-sub">Not started</div></div>
      </div>

      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
        <h3 style={{ fontFamily:'var(--font-display)', fontSize:16, fontWeight:500, color:'var(--text-primary)' }}>Automation workflows</h3>
        <button className="btn btn-primary" onClick={() => setCreating(true)}>+ Create workflow</button>
      </div>

      {creating && (
        <div className="card" style={{ marginBottom:16, padding:'16px 20px' }}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:10 }}>
            <input className="search-input" placeholder="Workflow name *" value={newWf.name} onChange={e => setNewWf(p=>({...p,name:e.target.value}))} />
            <input className="search-input" placeholder="Trigger (e.g. Contact created)" value={newWf.trigger} onChange={e => setNewWf(p=>({...p,trigger:e.target.value}))} />
          </div>
          <input className="search-input" placeholder="Description" value={newWf.description} onChange={e => setNewWf(p=>({...p,description:e.target.value}))} style={{ width:'100%', marginBottom:10 }} />
          <div style={{ display:'flex', gap:8 }}>
            <button className="btn btn-primary" onClick={handleCreate}>Create</button>
            <button className="btn btn-ghost" onClick={() => setCreating(false)}>Cancel</button>
          </div>
        </div>
      )}

      {loading ? (
        <div style={{ padding:40, textAlign:'center', color:'var(--text-dim)', fontSize:13 }}>Loading workflows…</div>
      ) : workflows.length === 0 ? (
        <div style={{ padding:40, textAlign:'center', color:'var(--text-dim)', fontSize:13 }}>No workflows yet — create your first automation</div>
      ) : workflows.map(wf => (
        <div key={wf.id} className="card" style={{ marginBottom:12 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:8 }}>
            <div>
              <div style={{ fontWeight:500, fontSize:14, color:'var(--text-primary)', marginBottom:4 }}>{wf.name}</div>
              {wf.trigger && <div style={{ fontSize:12, color:'var(--text-dim)' }}>Trigger: {wf.trigger}</div>}
              {wf.description && <div style={{ fontSize:12, color:'var(--text-muted)', marginTop:4 }}>{wf.description}</div>}
            </div>
            <span className={statusBadge(wf.status)}>{wf.status}</span>
          </div>
          <div style={{ display:'flex', gap:8, marginTop:12 }}>
            <button className="btn btn-ghost" style={{ fontSize:11, padding:'4px 10px' }} onClick={() => toggleStatus(wf)}>
              {wf.status === 'Active' ? 'Pause' : 'Activate'}
            </button>
            <button className="btn btn-ghost" style={{ fontSize:11, padding:'4px 10px', color:'var(--danger)' }} onClick={() => handleDelete(wf.id)}>Delete</button>
          </div>
        </div>
      ))}
    </div>
  );
}
